import React, { useMemo, useState } from 'react';
import { fetchOpenAIChatCompletion } from '../lib/openaiClient';
import './SchoolIntel.css';
import hbsLogo from '../assets/HBS_LOGO.png';
import stanfordLogo from '../assets/STANFORD_LOGO.png';
import whartonLogo from '../assets/WHARTON_LOGO.png';
import columbiaLogo from '../assets/COLUMBIA_LOGO.png';
import mitLogo from '../assets/MIT_LOGO.png';
import chicagoBoothLogo from '../assets/CHICAGO_BOOTH_LOGO.png';
import kelloggLogo from '../assets/KELLOGG_LOGO.png';
import berkeleyHaasLogo from '../assets/BERKELEY_HAAS_LOGO.png';
import yaleSomLogo from '../assets/YALE_SOM_LOGO.png';
import dardenLogo from '../assets/DARDEN_LOGO.png';
import uclaAndersonLogo from '../assets/UCLA_ANDERSON_LOGO.png';
import emoryGoizuetaLogo from '../assets/EMORY_GOIZUETA_LOGO.png';

type SchoolIntelFeedback = {
  fitAngles: string[];
  nicheCourses: string[];
  professorsOrCenters: string[];
  clubsOrCommunities: string[];
  essayInterviewHooks: string[];
  verifySources: string[];
  summary: string;
};

const EMPTY_INTEL: SchoolIntelFeedback = {
  fitAngles: [],
  nicheCourses: [],
  professorsOrCenters: [],
  clubsOrCommunities: [],
  essayInterviewHooks: [],
  verifySources: [],
  summary: ''
};

const SCHOOL_OPTIONS = [
  { name: 'Harvard Business School', logo: hbsLogo },
  { name: 'Stanford Graduate School of Business', logo: stanfordLogo },
  { name: 'Wharton School', logo: whartonLogo },
  { name: 'Columbia Business School', logo: columbiaLogo },
  { name: 'MIT Sloan', logo: mitLogo },
  { name: 'Kellogg SOM', logo: kelloggLogo },
  { name: 'Chicago Booth', logo: chicagoBoothLogo },
  { name: 'Berkeley Haas', logo: berkeleyHaasLogo },
  { name: 'Yale SOM', logo: yaleSomLogo },
  { name: 'Darden School of Business', logo: dardenLogo },
  { name: 'UCLA Anderson School of Management', logo: uclaAndersonLogo },
  { name: 'Emory Goizueta Business School', logo: emoryGoizuetaLogo }
] as const;

function normalizeArray(value: unknown, max = 6): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((v) => (typeof v === 'string' ? v.trim() : ''))
    .filter(Boolean)
    .slice(0, max);
}

function parseIntel(raw: string): SchoolIntelFeedback {
  const trimmed = raw.trim();
  const first = trimmed.indexOf('{');
  const last = trimmed.lastIndexOf('}');
  const jsonText = first >= 0 && last > first ? trimmed.slice(first, last + 1) : trimmed;
  const parsed = JSON.parse(jsonText) as Partial<SchoolIntelFeedback>;
  return {
    fitAngles: normalizeArray(parsed.fitAngles, 5),
    nicheCourses: normalizeArray(parsed.nicheCourses, 6),
    professorsOrCenters: normalizeArray(parsed.professorsOrCenters, 6),
    clubsOrCommunities: normalizeArray(parsed.clubsOrCommunities, 6),
    essayInterviewHooks: normalizeArray(parsed.essayInterviewHooks, 6),
    verifySources: normalizeArray(parsed.verifySources, 6),
    summary: typeof parsed.summary === 'string' ? parsed.summary.trim() : ''
  };
}

const SchoolIntel: React.FC = () => {
  const [selectedSchool, setSelectedSchool] = useState<string>(SCHOOL_OPTIONS[0].name);
  const [goalsText, setGoalsText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [intel, setIntel] = useState<SchoolIntelFeedback>(EMPTY_INTEL);
  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<'success' | 'error' | null>(null);

  const canAnalyze = !isLoading && selectedSchool.trim().length > 0 && goalsText.trim().length > 0;
  const hasIntel =
    intel.summary.length > 0 ||
    intel.fitAngles.length > 0 ||
    intel.nicheCourses.length > 0 ||
    intel.professorsOrCenters.length > 0 ||
    intel.clubsOrCommunities.length > 0 ||
    intel.essayInterviewHooks.length > 0;

  const goalWordCount = useMemo(() => {
    const t = goalsText.trim();
    if (!t) return 0;
    return t.split(/\s+/).length;
  }, [goalsText]);

  const handleAnalyze = async () => {
    const trimmedGoals = goalsText.trim();
    if (!trimmedGoals || !selectedSchool) return;
    setIsLoading(true);
    setMessage(null);
    setMessageType(null);
    try {
      const completion = await fetchOpenAIChatCompletion({
        model: 'gpt-4o-mini',
        temperature: 0.2,
        max_tokens: 1300,
        messages: [
          {
            role: 'system',
            content: `You are an MBA school research strategist.
Return STRICT JSON only with keys:
{
  "fitAngles": string[],
  "nicheCourses": string[],
  "professorsOrCenters": string[],
  "clubsOrCommunities": string[],
  "essayInterviewHooks": string[],
  "verifySources": string[],
  "summary": string
}
Rules:
- Focus only on the selected school.
- Include specific, research-oriented recommendations aligned to the student's goals.
- If unsure of exact names, state likely categories and include verification pointers in verifySources.
- Keep each array concise (max 6 items).
- Summary should be 2-3 sentences and practical for applications.`
          },
          {
            role: 'user',
            content: `Selected school: ${selectedSchool}\n\nStudent goals/context:\n${trimmedGoals}`
          }
        ]
      });
      setIntel(parseIntel(completion));
      setMessage('School intel generated. Verify details on official school pages before final submission.');
      setMessageType('success');
    } catch (err) {
      console.error(err);
      setMessage('Could not generate school intel right now. Please try again.');
      setMessageType('error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="school-intel-page">
      <div className="school-intel-container">
        <header className="school-intel-header">
          <h1 className="school-intel-title">School Intel Tool (Beta)</h1>
          <p className="school-intel-subtitle">
            Pick one school at a time, add your goals, and get research angles for essays and interviews.
          </p>
        </header>

        <section className="school-intel-grid">
          <article className="school-intel-card">
            <div className="school-intel-card-head">
              <h2>Your Input</h2>
              <span>{goalWordCount} words</span>
            </div>

            <label className="school-intel-label">
              School (one at a time)
            </label>
            <div className="school-intel-school-picker" role="group" aria-label="Select one school">
              {SCHOOL_OPTIONS.map((school) => (
                <button
                  key={school.name}
                  type="button"
                  className={`school-intel-school-button ${selectedSchool === school.name ? 'active' : ''}`}
                  onClick={() => setSelectedSchool(school.name)}
                  title={school.name}
                  aria-pressed={selectedSchool === school.name}
                >
                  <img src={school.logo} alt="" className="school-intel-school-logo" />
                </button>
              ))}
            </div>

            <label htmlFor="school-intel-goals" className="school-intel-label">
              Goals & context
            </label>
            <textarea
              id="school-intel-goals"
              className="school-intel-input"
              value={goalsText}
              onChange={(e) => setGoalsText(e.target.value)}
              placeholder="Example: I'm targeting product management in climate tech. I led a student startup and interned at a fintech company. I need angles for school fit essays and interview stories."
            />

            <div className="school-intel-actions">
              <button
                type="button"
                className="school-intel-generate"
                disabled={!canAnalyze}
                onClick={() => void handleAnalyze()}
              >
                {isLoading ? 'Generating Intel...' : 'Generate School Intel'}
              </button>
            </div>
          </article>

          <article className="school-intel-card">
            <div className="school-intel-card-head">
              <h2>School Intel Output</h2>
              <span>{hasIntel ? 'Ready' : 'Empty'}</span>
            </div>

            {hasIntel ? (
              <div className="school-intel-results">
                <section className="school-intel-section">
                  <h3>Top Fit Angles</h3>
                  <ul>{intel.fitAngles.map((x) => <li key={x}>{x}</li>)}</ul>
                </section>
                <section className="school-intel-section">
                  <h3>Niche Courses to Investigate</h3>
                  <ul>{intel.nicheCourses.map((x) => <li key={x}>{x}</li>)}</ul>
                </section>
                <section className="school-intel-section">
                  <h3>Professors / Centers</h3>
                  <ul>{intel.professorsOrCenters.map((x) => <li key={x}>{x}</li>)}</ul>
                </section>
                <section className="school-intel-section">
                  <h3>Clubs & Communities</h3>
                  <ul>{intel.clubsOrCommunities.map((x) => <li key={x}>{x}</li>)}</ul>
                </section>
                <section className="school-intel-section">
                  <h3>Essay / Interview Hooks</h3>
                  <ul>{intel.essayInterviewHooks.map((x) => <li key={x}>{x}</li>)}</ul>
                </section>
                <section className="school-intel-section">
                  <h3>Verification Checklist</h3>
                  <ul>{intel.verifySources.map((x) => <li key={x}>{x}</li>)}</ul>
                </section>
                {intel.summary && (
                  <section className="school-intel-section">
                    <h3>Summary</h3>
                    <p>{intel.summary}</p>
                  </section>
                )}
              </div>
            ) : (
              <div className="school-intel-empty">
                School-specific intel will appear here (fit angles, courses, professors, and essay/interview hooks).
              </div>
            )}
          </article>
        </section>

        {message && (
          <div className={`school-intel-message ${messageType === 'error' ? 'error' : 'success'}`}>{message}</div>
        )}
      </div>
    </div>
  );
};

export default SchoolIntel;

